import type { ZodError } from "zod";
import { ANALYTICS_EVENTS } from "./events";
import type { AnalyticsDefaultProperties, AnalyticsEventName, AnalyticsEventProperties } from "./events";
import { enforcePiiDenyList } from "./piiDenyList";

/**
 * The only analytics surface the apps are allowed to depend on.
 *
 * Vendor-agnostic on purpose: moving from Mixpanel's own SDK to Freshpaint
 * changed one file behind this interface and no call site.
 *
 * There is deliberately no `setGroup` or `setGroupProperties` — Mixpanel Group
 * Analytics is a paid add-on that is not on the plan, so both would write
 * nowhere. Account-level rollup is handled outside Mixpanel, from our own
 * order data.
 */
export interface AnalyticsClient {
  /** Record a catalog event. Unknown names and invalid payloads fail the build. */
  track<TName extends AnalyticsEventName>(name: TName, properties: AnalyticsEventProperties<TName>): void;
  /**
   * Bind subsequent events to a known user, by `VendorContactId`.
   *
   * There is no traits argument and no user profile. Everything that would
   * have been a profile property is a root property on every event instead,
   * so it records what was true when the event fired rather than being
   * overwritten in place — and so there is no code path by which PII could
   * reach a Mixpanel profile.
   */
  identify(distinctId: string): void;
  /**
   * Clear identity and queued state.
   *
   * Call on sign-out, without exception. A shared terminal login in a roastery
   * is plausible, and without a reset every subsequent person's activity is
   * merged into the previous one's identity.
   */
  reset(): void;
}

export interface AnalyticsClientOptions {
  /**
   * Merged into every event root — `app`, `environment`, `isImpersonating`,
   * `vendorKey`, `region`, `role` and `tenureBand`.
   */
  defaultProperties?: AnalyticsDefaultProperties;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

export const describeIssues = (error: ZodError): string =>
  error.issues.map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`).join("; ");

/**
 * Wraps any client so payloads are screened and validated before they leave
 * the browser.
 *
 * Types already reject unknown event names and wrong-shaped payloads at build
 * time; this is the runtime backstop for values that only exist at runtime —
 * `as` casts, API responses, payloads built by string key. A failed payload is
 * dropped with a warning: analytics must never break the page it measures.
 */
export const withEventValidation = (client: AnalyticsClient): AnalyticsClient => ({
  // Every method is delegated explicitly rather than spread. Spreading copies
  // own enumerable properties only, so a class-instance client — a legitimate
  // shape for the provider's documented `client` prop — would arrive here with
  // its prototype methods missing and `reset()` would throw.
  track: <TName extends AnalyticsEventName>(name: TName, properties: AnalyticsEventProperties<TName>) => {
    // The deny-list runs BEFORE the schema, deliberately. Zod strips unknown
    // keys, so a smuggled `...order` spread would be dropped silently and
    // nobody would learn they had nearly sent a customer address.
    const screened = isRecord(properties) ? enforcePiiDenyList(name, properties) : properties;

    // A cast or a runtime-supplied string can name an event the catalog does
    // not own. Dereferencing `.schema` on that would throw and break the page
    // this wrapper exists to keep working, so check before reaching in.
    // `Object.hasOwn`, not truthiness: `"toString"` and `"__proto__"` resolve
    // through the prototype, so a truthy check would pass and then throw on
    // `.schema`.
    const definition = Object.hasOwn(ANALYTICS_EVENTS, name) ? (ANALYTICS_EVENTS[name] as (typeof ANALYTICS_EVENTS)[TName]) : undefined;
    if (!definition) {
      console.warn(`[analytics] dropped "${name}" — not in the event catalog`);
      return;
    }

    const result = definition.schema.safeParse(screened);
    if (!result.success) {
      // Only issue paths and messages are logged — never the values.
      console.warn(`[analytics] dropped "${name}" — invalid payload: ${describeIssues(result.error)}`);
      return;
    }

    // `ANALYTICS_EVENTS[name].schema` on a generic key widens `result.data` to
    // a union of every payload type, which TS will not relate back to
    // `AnalyticsEventProperties<TName>`. The `safeParse` above already
    // guarantees they match, so the delegate is asserted once, here.
    // Bound, not bare: invoking a detached method drops the receiver, so a
    // class-instance client whose `track` reads `this` would throw.
    const forward = client.track.bind(client) as (name: TName, properties: unknown) => void;
    forward(name, result.data);
  },
  // Nothing to screen or validate: the only argument is the distinct id. Called
  // as a member of `client` rather than detached, so a class-instance client
  // whose `identify` reads `this` keeps its receiver.
  identify: (distinctId: string) => client.identify(distinctId),
  reset: () => client.reset(),
});
